import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, PageHero } from "@/components/marketing/Layout";
import {
  ArrowUpRight,
  BookOpen,
  Rocket,
  Wrench,
  ShieldCheck,
  Sparkles,
  CreditCard,
  Network,
  MessageCircle,
  Mail,
  LifeBuoy,
} from "lucide-react";
import { SUPPORT_ARTICLES } from "@/lib/support-articles";
import { useAutoTranslate } from "@/components/AutoTranslate";

const CATEGORY_META: Record<string, { icon: typeof Rocket; desc: string }> = {
  "Getting started": {
    icon: Rocket,
    desc: "Create your account, connect your website and launch your first AI Twin in about a minute.",
  },
  "Knowledge & training": {
    icon: BookOpen,
    desc: "Feed your twin the right sources so every answer is grounded and cited.",
  },
  "Personality & tone": {
    icon: Sparkles,
    desc: "Tune how your twin sounds, what it refuses, and how it hands off to humans.",
  },
  "Leads & conversations": {
    icon: MessageCircle,
    desc: "Review transcripts, score leads and route them into your CRM.",
  },
  "Network & marketplace": {
    icon: Network,
    desc: "Publish your public profile and understand Twin-to-Twin discovery.",
  },
  "Billing & plans": {
    icon: CreditCard,
    desc: "Upgrade, downgrade, invoices and what counts as a conversation.",
  },
  "Security & privacy": {
    icon: ShieldCheck,
    desc: "Data residency, isolation, GDPR/FADP and the DPA.",
  },
  Troubleshooting: {
    icon: Wrench,
    desc: "The widget didn't load, the twin gave a wrong answer, a crawl failed — start here.",
  },
};

const CATEGORY_ORDER = [
  "Getting started",
  "Knowledge & training",
  "Personality & tone",
  "Leads & conversations",
  "Network & marketplace",
  "Billing & plans",
  "Security & privacy",
  "Troubleshooting",
];

const CATEGORIES = CATEGORY_ORDER.map((title) => ({
  title,
  ...CATEGORY_META[title],
  articles: SUPPORT_ARTICLES.filter((a) => a.category === title),
}));

const CHANNELS = [
  {
    icon: Mail,
    title: "Email support",
    desc: "Fastest way to reach a human. Usual reply time: within one business day.",
    action: { label: "hello@realitytwin.io", href: "mailto:hello@realitytwin.io" as const },
  },
  {
    icon: LifeBuoy,
    title: "Talk to sales",
    desc: "Enterprise, security review, procurement, custom volume or hosting region.",
    action: { label: "Contact sales", to: "/contact" as const },
  },
  {
    icon: ShieldCheck,
    title: "Report a security issue",
    desc: "Responsible disclosure — we reply within one business day and credit researchers.",
    action: { label: "hello@realitytwin.io", href: "mailto:hello@realitytwin.io" as const },
  },
];

export const Route = createFileRoute("/support/")({
  head: () => {
    const seo = buildSeo({
      path: "/support",
      title: "Support Center — Reality Twin help, guides & contact",
      description:
        "Get help with Reality Twin: guides for setup, knowledge, personality, billing, security and troubleshooting — plus how to reach a human.",
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Reality Twin Support Center",
            url: "https://realitytwin.io/support",
            description:
              "Support Center for Reality Twin — setup, knowledge, personality, billing, security and troubleshooting.",
            publisher: { "@type": "Organization", name: "Ruznic Marketing" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://realitytwin.io/" },
              {
                "@type": "ListItem",
                position: 2,
                name: "Support",
                item: "https://realitytwin.io/support",
              },
            ],
          }),
        },
      ],
    };
  },
  component: SupportPage,
});

function SupportPage() {
  // The whole page is authored in English; translate it into the reader's
  // language through the cached translator (icons/links/emails left intact).
  const CHROME = [
    "Support Center", // 0
    "Help, when you actually need it.", // 1
    "Browse guides by topic, or write to a human. Every ticket is answered by the team building the product.", // 2
    "article", // 3
    "articles", // 4
    "Talk to a human", // 5
    "No tiered support queues. No bots pretending to be people.", // 6
    "Looking for a specific answer?", // 7
    "Start with the", // 8
    "or the", // 9
    "If it's not covered,", // 10
    "— we'll add it.", // 11
    "FAQ", // 12
    "docs", // 13
    "reach out", // 14
  ];
  const catTitles = CATEGORY_ORDER;
  const catDescs = CATEGORY_ORDER.map((k) => CATEGORY_META[k].desc);
  const chanTitles = CHANNELS.map((ch) => ch.title);
  const chanDescs = CHANNELS.map((ch) => ch.desc);
  const chanLabels = CHANNELS.map((ch) => ch.action.label);
  const artTitles = SUPPORT_ARTICLES.map((a) => a.title);

  const src = [
    ...CHROME,
    ...catTitles,
    ...catDescs,
    ...chanTitles,
    ...chanDescs,
    ...chanLabels,
    ...artTitles,
  ];
  const { out } = useAutoTranslate(src);
  let p = 0;
  const C = CHROME.map(() => out[p++]);
  const catTitleT = catTitles.map(() => out[p++]);
  const catDescT = catDescs.map(() => out[p++]);
  const chanTitleT = chanTitles.map(() => out[p++]);
  const chanDescT = chanDescs.map(() => out[p++]);
  const chanLabelT = chanLabels.map(() => out[p++]);
  const artTitleBySlug = new Map(SUPPORT_ARTICLES.map((a) => [a.slug, out[p++]]));
  const catIndex = new Map(CATEGORY_ORDER.map((title, i) => [title, i]));

  return (
    <MarketingLayout>
      <PageHero eyebrow={C[0]} title={C[1]} sub={C[2]} />

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
            {CATEGORIES.map((c) => {
              const ci = catIndex.get(c.title) ?? 0;
              return (
                <div key={c.title} className="bg-card p-7 flex flex-col">
                  <div className="flex items-center justify-between">
                    <c.icon className="h-5 w-5" strokeWidth={1.5} />
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {c.articles.length} {c.articles.length === 1 ? C[3] : C[4]}
                    </span>
                  </div>
                  <h2 className="mt-6 font-serif text-[22px] leading-tight tracking-[-0.02em]">
                    {catTitleT[ci] ?? c.title}
                  </h2>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">
                    {catDescT[ci] ?? c.desc}
                  </p>
                  <ul className="mt-5 space-y-2 text-[13.5px]">
                    {c.articles.map((a) => (
                      <li key={a.slug}>
                        <Link
                          to="/support/$slug"
                          params={{ slug: a.slug }}
                          className="group inline-flex items-start gap-1.5 text-muted-foreground hover:text-foreground transition"
                        >
                          <span className="underline decoration-border underline-offset-4 group-hover:decoration-foreground">
                            {artTitleBySlug.get(a.slug) ?? a.title}
                          </span>
                          <ArrowUpRight className="h-3.5 w-3.5 mt-0.5 opacity-60 group-hover:opacity-100" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-border py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="font-mono-eyebrow">{C[5]}</div>
          <h2 className="mt-3 font-serif text-[40px] sm:text-[52px] leading-[1.02] tracking-[-0.025em] max-w-2xl">
            {C[6]}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
            {CHANNELS.map((ch, ci) => (
              <div key={ch.title} className="bg-background p-7 flex flex-col">
                <ch.icon className="h-5 w-5" strokeWidth={1.5} />
                <h3 className="mt-6 font-serif text-[22px] leading-tight tracking-[-0.02em]">
                  {chanTitleT[ci] ?? ch.title}
                </h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground flex-1">
                  {chanDescT[ci] ?? ch.desc}
                </p>
                <div className="mt-6">
                  {"href" in ch.action ? (
                    <a
                      href={ch.action.href}
                      className="inline-flex items-center gap-1 text-[13.5px] font-medium text-foreground underline underline-offset-4"
                    >
                      {chanLabelT[ci] ?? ch.action.label}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <Link
                      to={ch.action.to}
                      className="inline-flex items-center gap-1 text-[13.5px] font-medium text-foreground underline underline-offset-4"
                    >
                      {chanLabelT[ci] ?? ch.action.label}
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-[36px] leading-tight tracking-[-0.025em]">{C[7]}</h2>
          <p className="mt-4 text-[15px] text-muted-foreground">
            {C[8]}{" "}
            <Link to="/faq" className="underline text-foreground">
              {C[12]}
            </Link>{" "}
            {C[9]}{" "}
            <Link to="/docs" className="underline text-foreground">
              {C[13]}
            </Link>
            . {C[10]}{" "}
            <Link to="/contact" className="underline text-foreground">
              {C[14]}
            </Link>{" "}
            {C[11]}
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
